import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MessageCircle, MapPin, Instagram } from "lucide-react";
import { site } from "../content";
import { waLink, trackWhatsAppClick } from "../lib/whatsapp";
import { scrollToId } from "../lib/scroll";

const NAV = [
  { label: "Como funciona", id: "como-funciona" },
  { label: "Modelos", id: "modelos" },
  { label: "Especificações", id: "especificacoes" },
  { label: "FAQ", id: "faq" },
];

const PRIVACY_TEXT = `MODELO — revisar com assessoria jurídica antes da publicação oficial.

A ${site.companyName} (CNPJ ${site.cnpj}), responsável pelo Barber Steamer, respeita a sua privacidade. Ao clicar nos botões de WhatsApp deste site, você é direcionado ao aplicativo WhatsApp, e as informações compartilhadas na conversa (como nome, telefone e cidade) são utilizadas exclusivamente para atendimento, orçamento e suporte relacionados ao produto.

Não vendemos nem compartilhamos seus dados com terceiros para fins de marketing. Ferramentas de análise e publicidade (Google Analytics, Google Ads e Meta Pixel), quando ativadas, coletam dados de navegação de forma anonimizada para melhorar a experiência do site e medir campanhas.

Você pode solicitar a qualquer momento a confirmação, correção ou exclusão dos seus dados pessoais, conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), pelo WhatsApp ${site.whatsappDisplay}.`;

const TERMS_TEXT = `MODELO — revisar com assessoria jurídica antes da publicação oficial.

Este site é um canal de apresentação do Barber Steamer, equipamento produzido por ${site.companyName} (CNPJ ${site.cnpj}), Jundiaí – SP.

As especificações técnicas, prazos, condições de entrega e disponibilidade exibidos neste site têm caráter informativo e devem ser confirmados no momento da compra. Valores e condições comerciais são informados exclusivamente pelo atendimento oficial via WhatsApp ${site.whatsappDisplay}.

A garantia de 12 meses cobre defeitos de fabricação, conforme condições informadas no ato da compra e no manual do produto.`;

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-ink/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-6 pb-28 pt-16 md:px-12 md:pb-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <img
              src={site.assets.logo}
              alt="Barber Steamer — o verdadeiro aquecedor de toalhas para barbearia"
              className="h-16 w-auto"
            />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-bone/50">
              Solução profissional para o preparo de toalhas quentes na barbearia.
              Desenvolvido e produzido no Brasil.
            </p>
            <a
              href={waLink(site.messages.floating)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              onClick={() => trackWhatsAppClick("footer", "WhatsApp footer")}
              className="mt-6 inline-flex items-center gap-2.5 text-sm font-semibold text-bone/70 transition-colors duration-300 hover:text-gold"
            >
              <MessageCircle className="h-4 w-4 text-gold" aria-hidden="true" />
              WhatsApp {site.whatsappDisplay}
            </a>
            <p className="mt-3 flex items-center gap-2.5 text-sm text-bone/50">
              <MapPin className="h-4 w-4 text-gold" aria-hidden="true" />
              {site.location} — atendimento em todo o Brasil
            </p>
            <a
              href="https://www.instagram.com/barbersteamer_/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="mt-3 inline-flex items-center gap-2.5 text-sm font-semibold text-bone/70 transition-colors duration-300 hover:text-gold"
            >
              <Instagram className="h-4 w-4 text-gold" aria-hidden="true" />
              @barbersteamer_
            </a>
          </div>

          <nav aria-label="Links do site">
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-bone/40">
              Navegação
            </p>
            <ul className="mt-5 space-y-3">
              {NAV.map((item) => (
                <li key={item.id}>
                  <button
                    data-testid={`footer-nav-${item.id}`}
                    onClick={() => scrollToId(item.id)}
                    className="text-sm text-bone/55 transition-colors duration-300 hover:text-gold"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-bone/40">
              Institucional
            </p>
            <ul className="mt-5 space-y-3">
              <li>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      data-testid="footer-privacy-link"
                      className="text-sm text-bone/55 transition-colors duration-300 hover:text-gold"
                    >
                      Política de Privacidade
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[80vh] overflow-y-auto border-white/10 bg-graphite text-bone">
                    <DialogHeader>
                      <DialogTitle className="font-display text-bone">Política de Privacidade</DialogTitle>
                    </DialogHeader>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-bone/65">{PRIVACY_TEXT}</p>
                  </DialogContent>
                </Dialog>
              </li>
              <li>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      data-testid="footer-terms-link"
                      className="text-sm text-bone/55 transition-colors duration-300 hover:text-gold"
                    >
                      Termos de Uso
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[80vh] overflow-y-auto border-white/10 bg-graphite text-bone">
                    <DialogHeader>
                      <DialogTitle className="font-display text-bone">Termos de Uso</DialogTitle>
                    </DialogHeader>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-bone/65">{TERMS_TEXT}</p>
                  </DialogContent>
                </Dialog>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/[0.07] pt-8 text-center text-xs text-bone/40 md:flex-row md:text-left">
          <p data-testid="footer-company-info">
            {site.companyName} • CNPJ {site.cnpj} • {site.location}
          </p>
          <p>© {new Date().getFullYear()} {site.brandName}. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
}
